import type { ColDef, GetQuickFilterTextParams, ValueGetterParams } from 'ag-grid-community';
import { compact, get, isArray } from 'lodash-es';
import { useMemo } from 'react';
import type { Security } from '../../../types/Security';
import {
  DEFAULT_SECURITY_IDENTIFIER_METADATA_KEYS,
  getCachedSecurityMetadata,
  getSecurityIdentifierSearchText,
  type SecurityIdentifierMetadataKey,
} from '../../../utils/getSecurityMetadata';
import { useBlotterTableContext } from '../BlotterTableContext';
import { appendUniqueQuickFilterTerms } from '../quickFilterSearchText';
import { defaultGetQuickFilterText } from '../utils';
import type { Column } from './types';

export {
  getSecurityIdentifierColumnMetadataKeys,
  getSecurityIdentifierSearchText,
  SECURITY_IDENTIFIER_METADATA_KEYS,
  type SecurityIdentifierMetadataKey,
} from '../../../utils/getSecurityMetadata';

const IDENTIFIER_COLUMN_DEFS = [
  { id: 'FIGI', title: 'OpenFIGI', metadataKey: 'FIGI' },
  { id: 'CUSIP', title: 'CUSIP', metadataKey: 'CUSIP' },
  { id: 'ISIN', title: 'ISIN', metadataKey: 'ISIN' },
  { id: 'SEDOL', title: 'SEDOL', metadataKey: 'SEDOL' },
] as const satisfies ReadonlyArray<{
  id: SecurityIdentifierMetadataKey;
  title: string;
  metadataKey: SecurityIdentifierMetadataKey;
}>;

function getMetadataIdentifierValue(
  metadata: ReturnType<typeof getCachedSecurityMetadata>,
  key: SecurityIdentifierMetadataKey
): string | undefined {
  const value = metadata[key];
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed !== '' ? trimmed : undefined;
}

export function getSecuritySymbolSearchText(security: Security | undefined, symbolValue: string | undefined): string {
  const parts: string[] = [];

  if (symbolValue) {
    parts.push(symbolValue);
  }

  if (security) {
    const displaySymbol = security.DisplaySymbol;
    if (displaySymbol && displaySymbol !== symbolValue) {
      parts.push(displaySymbol);
    }

    const identifierSearchText = getSecurityIdentifierSearchText(security);
    if (identifierSearchText) {
      parts.push(identifierSearchText);
    }
  }

  return parts.join(' ');
}

function getIdentifierValueForSymbol(
  params: ValueGetterParams<unknown>,
  symbolField: string,
  metadataKey: SecurityIdentifierMetadataKey
): string | undefined {
  if (!params.node?.data) {
    return undefined;
  }

  const symbol = get(params.node.data, symbolField);
  if (typeof symbol !== 'string' || symbol === '') {
    return undefined;
  }

  const security = params.context.current.securitiesBySymbol?.get(symbol);
  if (!security) {
    return undefined;
  }

  return getMetadataIdentifierValue(getCachedSecurityMetadata(security), metadataKey);
}

export interface GetSecurityIdentifierColumnsParams {
  symbolField: string;
  metadataKeys?: readonly SecurityIdentifierMetadataKey[];
}

/**
 * Hidden identifier columns for the column picker. Uses `custom` so the lookup
 * `valueGetter` survives Kyoko `columnTypes` conversion (`text` would drop it).
 */
export function getSecurityIdentifierColumns({
  symbolField,
  metadataKeys = DEFAULT_SECURITY_IDENTIFIER_METADATA_KEYS,
}: GetSecurityIdentifierColumnsParams): Column<ColDef>[] {
  const enabledMetadataKeys = new Set(metadataKeys);

  return IDENTIFIER_COLUMN_DEFS.filter(({ metadataKey }) => enabledMetadataKeys.has(metadataKey)).map(
    ({ id, title, metadataKey }) => ({
      id,
      field: id,
      type: 'custom' as const,
      title,
      initialHide: true,
      params: {
        valueGetter: (params: ValueGetterParams<unknown>) =>
          getIdentifierValueForSymbol(params, symbolField, metadataKey),
      },
    })
  );
}

export function useSecurityIdentifierColumns(symbolField: string): Column<ColDef>[] {
  const { settings } = useBlotterTableContext();
  const metadataKeys = settings?.securityIdentifierColumnKeys ?? DEFAULT_SECURITY_IDENTIFIER_METADATA_KEYS;

  return useMemo(() => getSecurityIdentifierColumns({ symbolField, metadataKeys }), [metadataKeys, symbolField]);
}

/** Wraps a quick-filter getter to append security identifiers for symbol values. */
export function wrapSecurityIdentifierQuickFilterText(
  getQuickFilterText: (params: GetQuickFilterTextParams) => string = defaultGetQuickFilterText
) {
  return (params: GetQuickFilterTextParams): string => {
    const baseText = getQuickFilterText(params);
    const value = params.value;
    if (value == null || value === '') {
      return baseText;
    }

    const symbols = isArray(value) ? value.filter((symbol): symbol is string => typeof symbol === 'string') : [value];
    if (symbols.length === 0 || typeof symbols[0] !== 'string') {
      return baseText;
    }

    const identifierSearchText = compact(
      symbols.map(symbol => getSecurityIdentifierSearchText(params.context.current.securitiesBySymbol?.get(symbol)))
    ).join(' ');

    if (!identifierSearchText) {
      return baseText;
    }

    return appendUniqueQuickFilterTerms(baseText, identifierSearchText);
  };
}
